/**
 * PlanPanel — the agent's current plan, rendered inside the right panel's
 * "Plan" tab (see ``RightPanel.tsx``).
 *
 * Populated from ``TodoWrite`` invocations (see ``session_manager.py`` →
 * ``todos_updated`` WebSocket event) and rehydrated from message history
 * when a conversation is reopened.
 *
 * It used to be a collapsible block pinned to the top of the chat; it now
 * lives in the sidebar so the conversation column stays uncluttered. The
 * tab itself provides the show/hide affordance, so this component is a
 * plain header (state + progress) followed by the checklist — no internal
 * collapse toggle.
 */

import { useMemo } from "react";
import { useChatStore, type AgentTodo } from "@/stores/chatStore";

export function PlanPanel() {
  const todos = useChatStore((s) => s.todos);

  const counts = useMemo(() => {
    const c = { pending: 0, in_progress: 0, completed: 0 };
    for (const t of todos) {
      if (t.status === "pending") c.pending++;
      else if (t.status === "in_progress") c.in_progress++;
      else if (t.status === "completed") c.completed++;
    }
    return c;
  }, [todos]);

  if (todos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center px-6">
        <div className="w-12 h-12 rounded-xl bg-space-20 flex items-center justify-center mb-3">
          <svg viewBox="0 0 16 16" fill="none" className="h-5 w-5 text-space-40">
            <path d="M4 4h8M4 8h8M4 12h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </div>
        <p className="text-sm text-space-50">No active plan</p>
        <p className="text-xs text-space-40 mt-1">
          When the agent plans multi-step work, its checklist shows up here.
        </p>
      </div>
    );
  }

  const done = counts.completed;
  const total = todos.length;
  const inProgress = counts.in_progress > 0;
  const allDone = done === total && total > 0;

  // Header label adapts to the current plan state:
  //   • running → "Working on…"
  //   • all done → "Plan complete"
  //   • idle/pending → "Plan"
  const headerLabel = allDone
    ? "Plan complete"
    : inProgress
      ? "Working on…"
      : "Plan";

  return (
    <div role="region" aria-label="Agent plan">
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3">
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md
            ${allDone ? "bg-oxy-50 text-oxy" : inProgress ? "bg-jove-30 text-jove" : "bg-space-20 text-space-50"}`}
        >
          {allDone ? (
            <svg viewBox="0 0 16 16" fill="none" className="h-3 w-3">
              <path d="m4 8 3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" fill="none" className="h-3 w-3">
              <path d="M4 4h8M4 8h8M4 12h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          )}
        </span>

        <span className="flex-1 truncate text-[13px] font-medium text-space">
          {headerLabel}
        </span>

        <span className="shrink-0 text-[11.5px] font-medium tabular-nums text-space-50">
          {done}/{total} done
        </span>
      </div>

      {/* Progress bar */}
      <div className="relative h-[2px] w-full overflow-hidden bg-space-30/50">
        <div
          className={`h-full transition-[width] duration-500 ease-out
            ${allDone ? "bg-oxy" : "bg-jove"}`}
          style={{ width: `${total === 0 ? 0 : (done / total) * 100}%` }}
        />
      </div>

      {/* Checklist */}
      <ol className="flex flex-col gap-0.5 px-2 py-2">
        {todos.map((t, idx) => (
          <TodoRow key={`${idx}-${t.content}`} todo={t} />
        ))}
      </ol>
    </div>
  );
}

function TodoRow({ todo }: { todo: AgentTodo }) {
  const isDone = todo.status === "completed";
  const isRunning = todo.status === "in_progress";
  // When running, we prefer the activeForm ("Reading candidate profile")
  // over the imperative content ("Read candidate profile") — it reads
  // more naturally as a live status.
  const label = isRunning && todo.activeForm ? todo.activeForm : todo.content;

  return (
    <li
      className={`flex items-start gap-3 rounded-lg px-2 py-1.5 transition-colors
        ${isRunning ? "bg-jove-10" : ""}`}
    >
      <StatusGlyph status={todo.status} />
      <span
        className={`flex-1 text-[13px] leading-snug transition-colors
          ${
            isDone
              ? "text-space-40 line-through decoration-space-40/60"
              : isRunning
                ? "text-space"
                : "text-space-50"
          }`}
      >
        {label}
      </span>
    </li>
  );
}

function StatusGlyph({ status }: { status: AgentTodo["status"] }) {
  if (status === "completed") {
    return (
      <span
        className="mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-oxy text-white"
        aria-label="Completed"
      >
        <svg viewBox="0 0 16 16" fill="none" className="h-2.5 w-2.5">
          <path
            d="m4 8 3 3 5-6"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }
  if (status === "in_progress") {
    return (
      <span
        className="mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center"
        aria-label="In progress"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inset-0 animate-ping rounded-full bg-jove/40" />
          <span className="relative h-2.5 w-2.5 rounded-full bg-jove" />
        </span>
      </span>
    );
  }
  return (
    <span
      className="mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center"
      aria-label="Pending"
    >
      <span className="h-2.5 w-2.5 rounded-full border border-space-40 bg-surface" />
    </span>
  );
}
